import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { UserIdleService } from 'angular-user-idle';
import {
  auditTime,
  BehaviorSubject,
  fromEvent,
  interval,
  merge,
  Subscription,
} from 'rxjs';
import { AuthService } from 'src/app/features/auth/services/auth.service';
import {
  APP_TIMEOUT_MINUTES,
  NO_HEADER_ROUTES,
  TOKEN_REFRESH_INTERVAL_MINUTES,
} from 'src/app/shared/app.constants';

import { ConfirmationDialogComponent } from '../../components/confirmation-dialog/confirmation-dialog.component';
import { MonitoringModeCountdownDialogComponent } from '../../components/monitoring-mode-countdown-dialog/monitoring-mode-countdown-dialog.component';
import { AbilityService } from '../ability/ability.service';
import { LogoutService } from '../logout.service';
import { NotificationService } from '../notification.service';
import { StorageBrowser } from '../storage.service';
import { MonitoringOverlayService } from './monitoring-mode.service';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private idleSubs: Subscription[] = [];
  private activitySub?: Subscription;
  private tokenRefreshSub?: Subscription;
  private stepUpDialogRef: MatDialogRef<ConfirmationDialogComponent> | null =
    null;
  private countdownDialogRef: MatDialogRef<MonitoringModeCountdownDialogComponent> | null =
    null;
  private isInCountdownProcess = false;
  public isInMonitorMode$: BehaviorSubject<boolean>;

  constructor(
    private monitoringOverlayService: MonitoringOverlayService,
    private storage: StorageBrowser,
    private logoutService: LogoutService,
    private userIdle: UserIdleService,
    private dialog: MatDialog,
    private authService: AuthService,
    private notificationService: NotificationService,
    private router: Router,
    private ability: AbilityService,
  ) {
    // Restore monitor mode state from localStorage on initialization
    const savedMonitorMode = !!this.storage.get('monitor_token');
    this.isInMonitorMode$ = new BehaviorSubject<boolean>(savedMonitorMode);
    console.log(
      `[SessionService] Restored monitor mode from localStorage: ${savedMonitorMode}`,
    );

    if (savedMonitorMode) {
      this.monitoringOverlayService.enable(() => {
        if (this.isStepUpModalOpen()) return;
        this.showStepUpModal('write');
      });
    }

    const idleSeconds = APP_TIMEOUT_MINUTES * 60;

    const timeoutSeconds = 5;
    const pingSeconds = 5;

    console.log(
      `[SessionService] Configured: idle=${idleSeconds}s timeout=${timeoutSeconds}s ping=${pingSeconds}s`,
    );

    this.userIdle.setConfigValues({
      idle: idleSeconds,
      timeout: timeoutSeconds,
      ping: pingSeconds,
    });

    // Track real user activity (mousemove/keydown/etc) and update last_active.
    // auditTime reduces log spam and localStorage writes.
    const activity$ = merge(
      fromEvent(window, 'mousemove'),
      fromEvent(window, 'mousedown'),
      fromEvent(window, 'keydown'),
      fromEvent(window, 'scroll'),
      fromEvent(window, 'touchstart'),
    ).pipe(auditTime(1000));
    this.userIdle.setCustomActivityEvents(activity$);

    this.activitySub = activity$.subscribe(() => {
      localStorage.setItem('last_active', Date.now().toString());
      console.log('[SessionService] Activity: updated last_active');
    });
  }

  isSessionValid(): boolean {
    const user = this.storage.get('user');
    if (!user) return false;
    localStorage.setItem('last_active', Date.now().toString());
    return true;
  }

  public startIdleService() {
    console.log('[SessionService] Starting Idle Watcher...');
    this.stopIdleService(); // Stop  timers

    const cfg = this.userIdle.getConfigValue?.();
    if (cfg) console.log('[SessionService] userIdle config:', cfg);

    this.userIdle.startWatching(); // Start new idle timer
    this.startProactiveTokenRefresh();

    const subIdleStatus = this.userIdle
      .onIdleStatusChanged()
      .subscribe((isIdle) => {});

    const subTimer = this.userIdle.onTimerStart().subscribe((count) => {
      if (typeof count !== 'number') {
        console.log('[SessionService] TimerStart (non-number):', count);
        return;
      }
      console.log(`[SessionService] Countdown tick: t=${count}s`);
    });

    const subTimeout = this.userIdle.onTimeout().subscribe(() => {
      console.log('[SessionService] TIMEOUT event fired');

      // Prevent multiple timeout events while countdown is in progress
      if (this.isInCountdownProcess) {
        console.log(
          '[SessionService] Already in countdown process - ignoring additional timeout event',
        );
        return;
      }

      const lastActive = parseInt(
        localStorage.getItem('last_active') || '0',
        10,
      );
      const now = Date.now();
      const diffSeconds = (now - lastActive) / 1000;

      console.log(
        `[SessionService] last_active age=${diffSeconds.toFixed(3)}s`,
      );

      // Cross-tab grace (if another tab wrote last_active very recently)
      const idleSeconds = APP_TIMEOUT_MINUTES * 60;
      const graceSeconds = 10;

      if (diffSeconds < idleSeconds - graceSeconds) {
        console.log(
          '[SessionService] Another-tab activity detected -> resetTimer()',
        );
        this.userIdle.resetTimer();
        return;
      }
      const isAlreadyMonitoring = this.isInMonitorMode$.getValue();
      if (isAlreadyMonitoring) {
        return;
      }

      // Check if user is on live-tests screen and NOT already in monitor mode
      const isOnLiveTests = this.router.url.includes('/live-tests');

      if (isOnLiveTests) {
        console.log(
          '[SessionService] On live-tests screen - showing countdown modal instead of logout',
        );
        this.showMonitoringModeCountdownModal();
        return;
      }

      console.log('[SessionService] Logging out due to inactivity');
      // SSO sessions are temporary and tied to a main lab — kick the user
      // back home instead of dropping them on the sub-lab's /auth/login
      // (where a SHARED_PHYSICIAN association can't log in anyway).
      if (this.ability.isSSO && this.ability.mainLabUrl) {
        this.logoutService.logoutAndRedirect(this.ability.mainLabUrl);
        return;
      }
      this.logoutService.logout(
        'Timeout: You have been logged out due to inactivity.',
      );
    });

    this.idleSubs.push(subIdleStatus, subTimer, subTimeout);
  }

  public hasSessionExpired(): boolean {
    const isCurrentlyMonitoring = this.isInMonitorMode$.getValue();
    if (isCurrentlyMonitoring) {
      return false;
    }

    const lastActiveRaw = localStorage.getItem('last_active');
    if (!lastActiveRaw) return true;

    const lastActive = parseInt(lastActiveRaw, 10);
    const now = Date.now();
    const diffSeconds = (now - lastActive) / 1000;

    return diffSeconds > APP_TIMEOUT_MINUTES * 60;
  }

  public stopIdleService() {
    console.log('[SessionService] Stopping Idle Watcher');

    this.userIdle.stopWatching();
    this.idleSubs.forEach((s) => s.unsubscribe());
    this.idleSubs = [];
    this.stopProactiveTokenRefresh();
  }

  /**
   * Periodically refresh the tokens while the session is active so a long-lived
   * session (e.g. multi-hour live streaming) doesn't die when the refresh-token
   * cookie ages out. Skipped in monitor mode (that uses a long-lived monitor
   * token) and once the user is gone. Terminal 400/401 ends the session; other
   * failures are retried on the next tick. See .
   *
   * The backend rotates the refresh token on every call with no grace window,
   * so two tabs refreshing at once would make one revoke the other
   * ("Refresh Token Invalid or Revoked"). We coordinate across tabs with the
   * Web Locks API plus a shared `last_token_refresh_at` timestamp, so only one
   * tab actually refreshes per interval; the others skip and reuse the token.
   */
  private startProactiveTokenRefresh() {
    this.stopProactiveTokenRefresh();

    // Treat session start as a fresh refresh point so we don't refresh again
    // immediately after login.
    localStorage.setItem(SessionService.LAST_REFRESH_KEY, Date.now().toString());

    const periodMs = TOKEN_REFRESH_INTERVAL_MINUTES * 60 * 1000;
    this.tokenRefreshSub = interval(periodMs).subscribe(() => {
      void this.runProactiveRefreshTick(periodMs);
    });
  }

  private stopProactiveTokenRefresh() {
    this.tokenRefreshSub?.unsubscribe();
    this.tokenRefreshSub = undefined;
  }

  private static readonly LAST_REFRESH_KEY = 'last_token_refresh_at';
  private static readonly REFRESH_LOCK_NAME = 'dormotech_token_refresh';

  private async runProactiveRefreshTick(periodMs: number): Promise<void> {
    if (this.isInMonitorMode$.getValue()) return;
    if (!this.storage.get('user')) return;

    // Skip if any tab (proactive or the interceptor's reactive refresh) already
    // refreshed within this interval.
    const margin = 30 * 1000;
    const isStale = () => {
      const last = parseInt(
        localStorage.getItem(SessionService.LAST_REFRESH_KEY) || '0',
        10,
      );
      return Date.now() - last >= periodMs - margin;
    };

    const doRefresh = () =>
      new Promise<void>((resolve) => {
        if (!isStale()) {
          resolve(); // another tab already refreshed
          return;
        }
        this.authService.refreshToken().subscribe({
          next: (res) => {
            if (res?.token) this.storage.set('token', res.token);
            localStorage.setItem(
              SessionService.LAST_REFRESH_KEY,
              Date.now().toString(),
            );
            console.log('[SessionService] Proactive token refresh ok');
            resolve();
          },
          error: (err) => {
            console.warn(
              '[SessionService] Proactive token refresh failed:',
              err?.status,
            );
            if (err?.status === 400 || err?.status === 401) {
              this.logout('Session expired. Please login again.');
            }
            resolve();
          },
        });
      });

    // True cross-tab mutual exclusion when available; otherwise fall back to the
    // timestamp check alone (small race, mitigated by in-tab single-flight).
    const locks: any = (navigator as any).locks;
    if (locks?.request) {
      try {
        await locks.request(
          SessionService.REFRESH_LOCK_NAME,
          { ifAvailable: true },
          async (lock: any) => {
            if (!lock) return; // another tab is refreshing right now
            await doRefresh();
          },
        );
      } catch {
        await doRefresh();
      }
    } else {
      await doRefresh();
    }
  }

  public isPublicRoute(url) {
    return NO_HEADER_ROUTES.some((route) => url.includes(route));
  }

  public ensureMonitoringMode(): boolean {
    const isCurrentlyMonitoring = this.isInMonitorMode$.getValue();
    if (isCurrentlyMonitoring) {
      this.showStepUpModal('exit');
      return true;
    }
    return false;
  }

  /**
   * Opens the step-up authentication modal
   * Triggered when user attempts a write operation in read-only monitoring mode
   * OR when user tries to exit monitoring mode.
   *
   * @param mode - 'write' (default) for write operations, 'exit' for exiting monitoring mode
   * @returns Observable with success status and token (if successful)
   */
  public showStepUpModal(mode: 'write' | 'exit' = 'write') {
    // Close any existing dialog first
    if (this.stepUpDialogRef) {
      console.log('[SessionService] Closing previously open step-up dialog');
      this.stepUpDialogRef.close({ success: false });
    }

    // Open new dialog with mode data
    this.stepUpDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      disableClose: false,
      panelClass: 'step-up-modal-panel',
      data: {
        title: 'Please Enter Password',
        message: '',
        mode,
        showPasswordField: true,
      },
    });

    // Handle dialog close
    this.stepUpDialogRef.afterClosed().subscribe((result) => {
      this.stepUpDialogRef = null;
      if (result?.confirmed) {
        console.log('[SessionService] Step-up successful:');
        this.isInMonitorMode$.next(false);

        this.notificationService.success(
          '✅ Authentication successful. You now have full access.',
        );
        this.exitMonitoringMode()
      }
    });
  }

  /**
   * Closes the step-up modal programmatically
   */
  public closeStepUpModal(): void {
    if (this.stepUpDialogRef) {
      console.log('[SessionService] Programmatically closing step-up modal');
      this.stepUpDialogRef.close({ success: false });
    }
  }

  /**
   * Checks if the step-up modal is currently open
   */
  public isStepUpModalOpen(): boolean {
    return !!this.stepUpDialogRef;
  }

  public logout(message?: string): void {
    this.isInMonitorMode$.next(false);
    if (this.ability.isSSO && this.ability.mainLabUrl) {
      this.logoutService.logoutAndRedirect(this.ability.mainLabUrl);
      return;
    }
    this.logoutService.logout(message);
  }

  /**
   * Enters monitoring mode by requesting a 24h read-only token
   * Updates session state and shows notification
   */
  public enterMonitoringMode(): void {
    console.log('[SessionService] Requesting monitor token');
    this.authService.requestMonitorToken().subscribe({
      next: (response) => {
        console.log('[SessionService] Monitor token received:', response);
        this.isInMonitorMode$.next(true);
        // Monitor mode state is automatically persisted via subscription
        this.notificationService.success(
          'You are now in read-only monitoring mode. Write operations will require password verification.',
        );
        this.monitoringOverlayService.enable(() => {
          if (this.isStepUpModalOpen()) return;
          this.showStepUpModal('write');
        });
        // Restart idle service in monitor mode (it will not logout while in monitor mode)
        this.startIdleService();
      },
      error: (error) => {
        console.error(
          '[SessionService] Failed to enter monitoring mode:',
          error,
        );
        this.notificationService.error(
          'Failed to enter monitoring mode. Please try again.',
        );
        // Restart idle service to continue monitoring for timeout
        this.startIdleService();
      },
    });
  }

  /**
   * Shows the monitoring mode countdown modal
   * Called when user is inactive for 15 minutes on live-tests screen
   * Gives user 10 seconds to cancel before entering monitoring mode
   */
  private showMonitoringModeCountdownModal(): void {
    if (this.countdownDialogRef) {
      return;
    }

    console.log('[SessionService] Opening monitoring mode countdown modal');

    // Mark that we're in countdown process to prevent multiple timeouts
    this.isInCountdownProcess = true;

    // IMPORTANT: Stop the idle service to prevent it from firing another timeout
    // while we're showing the countdown dialog
    this.stopIdleService();

    // Open countdown dialog with no outside close
    this.countdownDialogRef = this.dialog.open(
      MonitoringModeCountdownDialogComponent,
      {
        disableClose: true,
        panelClass: 'monitoring-mode-countdown-panel',
        width: '400px',
      },
    );

    // Safety timeout: if dialog doesn't close within 30 seconds, clear the flag and restart idle service
    const safetyTimeout = setTimeout(() => {
      if (this.isInCountdownProcess) {
        console.warn(
          '[SessionService] Countdown process safety timeout - clearing flag and restarting idle service',
        );
        this.isInCountdownProcess = false;
        this.startIdleService();
      }
    }, 30000);

    // Handle dialog close
    this.countdownDialogRef.afterClosed().subscribe((result) => {
      // Clear the safety timeout
      clearTimeout(safetyTimeout);

      // Clear the countdown process flag
      this.isInCountdownProcess = false;

      if (result?.action === 'cancel') {
        console.log(
          '[SessionService] User cancelled monitoring mode countdown',
        );
        // Reset idle timer and stay in normal mode
        this.startIdleService(); // Restart idle service
        this.notificationService.success(
          'Idle timer reset. Continuing normal monitoring.',
        );
      } else if (result?.action === 'auto-confirm') {
        console.log(
          '[SessionService] Countdown finished - auto-entering monitoring mode',
        );
        // Countdown finished, enter monitoring mode
        this.enterMonitoringMode();
        // Note: idle service will be restarted by enterMonitoringMode
      }
    });
  }

  /**
   * Exits monitoring mode explicitly
   * Clears the monitor mode state from memory and localStorage
   */
  public exitMonitoringMode(): void {
    console.log('[SessionService] Exiting monitor mode');
    this.isInMonitorMode$.next(false);
    this.monitoringOverlayService.disable();
    // Monitor mode state is automatically persisted via subscription
  }
}
